import type { Supplier } from "@/context/FinanceContext";
import { PersistedState } from "./useFinanceState";
import { toast } from "@/hooks/use-toast";

export function useSuppliersOperations(
  state: PersistedState,
  setState: React.Dispatch<React.SetStateAction<PersistedState>>,
  user: any,
  createSupplier: (data: any) => Promise<string>,
  updateSupabaseSupplier: (id: string, data: any) => Promise<boolean>,
  deleteSupplier: (id: string) => Promise<boolean>
) {
  const addSupplier = async (name: string, extra?: { cnpj?: string; anexo?: string }): Promise<string | null> => {
    const existingSupplier = state.suppliers.find(s => s.name.toLowerCase() === name.toLowerCase());
    if (existingSupplier) {
      toast({
        variant: "destructive",
        title: "Nome duplicado",
        description: "Já existe um fornecedor com este nome.",
      });
      return null;
    }
    
    const tempId = `s-${crypto.randomUUID()}`;
    
    if (user) {
      try {
        const supplierId = await createSupplier({ name, cnpj: extra?.cnpj || null, anexo: extra?.anexo || null });
        
        if (supplierId) {
          setState((s) => ({
            ...s,
            suppliers: [...s.suppliers, { id: supplierId, name, cnpj: extra?.cnpj, anexo: extra?.anexo }],
          }));
          
          toast({
            title: "Fornecedor adicionado",
            description: `${name} foi adicionado com sucesso.`,
          });
          return supplierId;
        }
      } catch (error) {
        console.error('Error syncing supplier to Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível salvar o fornecedor na nuvem.",
        });
        return null;
      }
    } else {
      setState((s) => ({
        ...s,
        suppliers: [...s.suppliers, { id: tempId, name, cnpj: extra?.cnpj, anexo: extra?.anexo }],
      }));
      
      toast({
        title: "Fornecedor adicionado localmente",
        description: `${name} foi adicionado. Faça login para sincronizar com a nuvem.`,
      });
      return tempId;
    }
    return null;
  };

  const updateSupplier = async (id: string, name: string, extra?: { cnpj?: string; anexo?: string }) => {
    // Update local state first
    setState((s) => ({
      ...s,
      suppliers: s.suppliers.map((s) =>
        s.id === id
          ? { ...s, name, ...(extra ? { cnpj: extra.cnpj, anexo: extra.anexo } : {}) }
          : s
      ),
    }));

    // Sync to Supabase if user is logged in
    if (user) {
      try {
        await updateSupabaseSupplier(id, {
          name,
          ...(extra ? { cnpj: extra.cnpj ?? null, anexo: extra.anexo ?? null } : {}),
        });
      } catch (error) {
        console.error('Error syncing supplier update to Supabase:', error);
      }
    }
  };

  const removeSupplier = async (id: string) => {
    // Sync to Supabase if user is logged in first
    if (user) {
      try {
        const success = await deleteSupplier(id);
        if (!success) {
          // Se a exclusão no Supabase falhou, não remove do estado local
          return;
        }
        
        toast({
          title: "Fornecedor excluído",
          description: "O fornecedor foi removido com sucesso.",
        });
      } catch (error) {
        console.error('Error deleting supplier from Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível excluir o fornecedor. Tente novamente.",
        });
        return;
      }
    }

    // Update local state only after successful Supabase deletion (or if offline)
    setState((s) => ({
      ...s,
      suppliers: s.suppliers.filter((s) => s.id !== id),
    }));

    if (!user) {
      toast({
        title: "Fornecedor excluído localmente",
        description: "O fornecedor foi removido localmente. Faça login para sincronizar.",
      });
    }
  };

  return {
    addSupplier,
    updateSupplier,
    removeSupplier,
  };
}
